import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ActionIcon, Box, Button, Center, Flex, Loader, Text, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowLeft } from "@tabler/icons-react";
import TitleComponent from "@/components/shared/TitleComponent";
import MatchedProfileView from "@/pages/profile/MatchedView";
import WorkspacePartnerSection from "@/pages/collaborations/components/WorkspacePartnerSection";
import CollaborationWorkspaceTabShell from "@/pages/collaborations/components/CollaborationWorkspaceTabShell";
import WorkspaceFeatureLockedPanel from "@/pages/collaborations/components/WorkspaceFeatureLockedPanel";
import InfoBanner from "@/components/shared/InfoBanner";
import { STATUS_COLORS } from "@/components/shared/constants/general.const";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import { resolveCompanyWorkspacePath } from "@/shared/utils/collaborationNavigation.util";
import {
  DISCOVERY_RETURN_TO,
  resolveWorkspaceBackNavigation,
} from "@/shared/utils/workspaceBackNavigation.util";

/** Logged-in preview workspace before or without a collaboration match (express interest lives here). */
export default function CollaborationCompanyWorkspacePage() {
  const { companyId } = useParams() as { companyId: string };
  const navigate = useNavigate();
  const location = useLocation();
  const { hasProfile } = useUserContext();

  const [company, setCompany] = useState<any>(null);
  const [collaboration, setCollaboration] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("overview");
  const expressInterestOpenerRef = useRef<(() => void) | null>(null);

  const theme = useMantineTheme();
  const isWorkspaceMobile = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true },
  );

  const discoverReturnTo = (location.state as { discoverReturnTo?: string } | null)?.discoverReturnTo;

  const workspaceBack = useMemo(
    () => resolveWorkspaceBackNavigation(discoverReturnTo, DISCOVERY_RETURN_TO),
    [discoverReturnTo],
  );

  const handleBack = useCallback(() => {
    navigate(workspaceBack.path);
  }, [navigate, workspaceBack.path]);

  const load = useCallback(async () => {
    if (!companyId || hasProfile === null) return;
    setLoading(true);
    try {
      const workspacePath = await resolveCompanyWorkspacePath(companyId);
      if (workspacePath.startsWith("/collaborations/") && !workspacePath.includes("/company/")) {
        navigate(workspacePath, { replace: true, state: location.state });
        return;
      }

      const [companyData, collab] = await Promise.all([
        trpc.company.publicGetById.query({ companyId }),
        trpc.collaboration.getByCompanyId.query({ targetCompanyId: companyId }).catch(() => null),
      ]);
      setCompany(companyData);
      setCollaboration(collab);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, hasProfile, navigate, location.state]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleMatchCreated = (matchId: string) => {
    navigate(`/collaborations/${matchId}`, { replace: true, state: location.state });
  };

  const openExpressInterest = () => {
    expressInterestOpenerRef.current?.();
  };

  const lockedFeatureDescription =
    "Express interest to start this collaboration. Assessment (questionnaire & chat) and shared documents unlock after the request is accepted and both companies pay.";

  if (loading) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Loader size="lg" />
        </Center>
      </Box>
    );
  }

  if (!company) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Text c="slate.6">Company not found</Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        ...(isWorkspaceMobile ? { minHeight: "100vh" } : { height: "100vh" }),
      }}
    >
      <Flex
        align="center"
        gap={{ base: "xs", sm: "md" }}
        px={{ base: "sm", sm: "md" }}
        py={{ base: "sm", sm: "md" }}
        wrap="nowrap"
        style={{ borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}
      >
        {isWorkspaceMobile ? (
          <ActionIcon
            variant="filled"
            color="primary"
            size="lg"
            radius="xl"
            onClick={handleBack}
            aria-label="Back to Discovery"
          >
            <IconArrowLeft size={18} />
          </ActionIcon>
        ) : (
          <Button
            variant="filled"
            color="primary"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            radius="xl"
            onClick={handleBack}
            style={{ flexShrink: 0 }}
          >
            {workspaceBack.label}
          </Button>
        )}
        <Text
          fw={500}
          fz={{ base: "md", sm: "xl" }}
          lh={1.25}
          style={{ flex: 1, minWidth: 0, textAlign: isWorkspaceMobile ? "center" : undefined }}
        >
          Collaboration Workspace
        </Text>
      </Flex>

      <Box
        px="md"
        pt="md"
        pb={isWorkspaceMobile ? "xl" : undefined}
        style={{
          flex: isWorkspaceMobile ? undefined : 1,
          display: "flex",
          flexDirection: "column",
          minHeight: isWorkspaceMobile ? undefined : 0,
        }}
      >
        {!collaboration ? (
          <Box mb="md">
            <InfoBanner
              title="Start a collaboration"
              subtitle="You are viewing company’s anonymous profile. Express interest to unlock details, share documents, chat and partner readiness."
              bgColor={STATUS_COLORS.WARNING.bg}
              borderColor={STATUS_COLORS.WARNING.border}
              titleColor={STATUS_COLORS.WARNING.text}
              subtitleColor={STATUS_COLORS.WARNING.text}
              actionLabel="Express Interest"
              onAction={openExpressInterest}
            />
          </Box>
        ) : null}

        <WorkspacePartnerSection
          company={company}
          collaboration={collaboration}
          onCollaborationChange={setCollaboration}
          onMatchCreated={handleMatchCreated}
          expressInterestOpenerRef={expressInterestOpenerRef}
          hideCompanyHeaderCard={!collaboration}
        />

        <CollaborationWorkspaceTabShell
          activeTab={activeTab}
          onTabChange={setActiveTab}
          overviewPanel={
            <MatchedProfileView
              company={company}
              containerSize="fluid"
              mobileCollabWorkspaceCard
              anonymousView
              workspaceCollaboration={collaboration ?? undefined}
            />
          }
          assessmentPanel={
            <WorkspaceFeatureLockedPanel
              title="Assessment locked"
              description={lockedFeatureDescription}
              primaryActionLabel={!collaboration ? "Express Interest" : undefined}
              onPrimaryAction={!collaboration ? openExpressInterest : undefined}
            />
          }
          documentsPanel={
            <WorkspaceFeatureLockedPanel
              title="Shared documents locked"
              description={lockedFeatureDescription}
              primaryActionLabel={!collaboration ? "Express Interest" : undefined}
              onPrimaryAction={!collaboration ? openExpressInterest : undefined}
            />
          }
        />
      </Box>
    </Box>
  );
}
